import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createWorkspace } from '../../services/firebaseService';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, WifiIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { isNetworkAvailable, firestore, ensureAuthentication, auth, checkFirestoreConnection } from '../../firebase';

const CreateWorkspace = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [workspaceName, setWorkspaceName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);
  const [firestoreStatus, setFirestoreStatus] = useState({
    checked: false,
    connected: false,
    error: null
  });
  
  // Add a ref to track submission status
  const isSubmittingRef = useRef(false);
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setWorkspaceName('');
      setDescription('');
      setError('');
      setIsLoading(false);
      // Reset submission tracking when modal opens
      isSubmittingRef.current = false;
    }
  }, [isOpen]);
  
  // Check Firestore connection when the modal opens
  useEffect(() => {
    if (isOpen) {
      const checkConnection = async () => {
        setFirestoreStatus({ checked: false, connected: false, error: null });
        setError('');
        
        try {
          // First ensure we have authentication
          const user = await ensureAuthentication();
          if (!user) {
            setError('Authentication failed. Please try again.');
            setFirestoreStatus({ checked: true, connected: false, error: 'Authentication failed' });
            return;
          }

          // Then check Firestore connection
          setFirestoreStatus({ 
            checked: true, 
            connected: connectionResult.connected,
            error: connectionResult.error || null
          });
          
          if (!connectionResult.connected) {
            setError(connectionResult.error || 'Failed to connect to Firebase');
          }
        } catch (error) {
          console.error('Error during connection check:', error);
          setFirestoreStatus({ checked: true, connected: false, error: error.message });
          setError(`Connection error: ${error.message}`);
        }
      };
      
      checkConnection();
    }
  }, [isOpen]);
  
  // Monitor network status
  useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus(true);
      // Recheck Firestore connection when we come back online
      checkFirestoreConnection().then(result => {
        setFirestoreStatus({ 
          checked: true, 
          connected: result.connected,
          error: result.error || null
        });
        
        if (result.connected) {
          setError('');
        }
      });
    };
    const handleOffline = () => setNetworkStatus(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Initial check
    setNetworkStatus(isNetworkAvailable());
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    // Add this debugging code
    console.log('Current auth state:', {
      user: auth.currentUser,
      isAuthenticated: !!auth.currentUser,
      uid: auth.currentUser?.uid
    });
    
    // Check Firestore connection
    checkFirestoreConnection().then(result => {
      console.log('Firestore connection check result:', result);
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Use the ref to prevent multiple submissions
    if (isSubmittingRef.current || isLoading) {
      console.log('Submission already in progress, ignoring duplicate submission');
      return;
    }
    
    // Set submission flag immediately
    isSubmittingRef.current = true;
    
    // Set loading state immediately
    setIsLoading(true);

    if (!workspaceName.trim()) {
      setError('Please enter a workspace name');
      setIsLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    // Check if Firestore is available
    if (!firestore) {
      setError('Firebase Firestore is not initialized. Please refresh the page and try again.');
      setIsLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    // Check if we're online before trying to create
    if (!networkStatus) {
      setError('Cannot create workspace while offline. Please check your internet connection and try again.');
      setIsLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    
    // Double-check Firebase connection
    try {
      if (!connectionResult.connected) {
        setError(`Cannot connect to Firebase: ${connectionResult.error}`);
        setIsLoading(false);
        isSubmittingRef.current = false;
        return;
      }
    } catch (connectionError) {
      setError(`Failed to verify Firebase connection: ${connectionError.message}`);
      setIsLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    setError('');

    // Set a timeout to prevent hanging indefinitely
    const timeoutId = setTimeout(() => {
      if (isSubmittingRef.current) {
        setIsLoading(false);
        isSubmittingRef.current = false;
        setError('Request timed out. The workspace may have been created. Please check your workspaces list.');
      }
    }, 15000); // 15 second timeout

    try {
      const workspaceData = {
        name: workspaceName.trim(),
        description: description.trim(),
      };

      console.log('Creating workspace with data:', workspaceData);
      console.log('Current user:', currentUser?.uid);
      
      // Create the workspace
      const workspace = await createWorkspace(workspaceData, currentUser.uid);
      console.log('Created workspace:', workspace);
      
      // Clear timeout since the request completed
      clearTimeout(timeoutId);
      
      // Store the workspace URL for redirection after modal closes
      const workspaceUrl = `/workspaces/${workspace.id}`;
      localStorage.setItem('lastCreatedWorkspace', workspaceUrl);
      
      // Reset form state first
      setWorkspaceName('');
      setDescription('');
      setError('');
      
      // Wait a moment to ensure the workspace is fully created in Firestore
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // First close the modal
      onClose();
      
      // Small delay before navigation to ensure modal is closed properly
      setTimeout(() => {
        navigate(`/workspaces/${workspace.id}`);
      }, 100);
    } catch (err) {
      console.error('Error creating workspace:', err);
      clearTimeout(timeoutId);
      
      // Handle specific errors
      if (err.message?.includes('offline') || err.message?.includes('network')) {
        setError('You appear to be offline. Please check your internet connection and try again.');
      } else if (err.message?.includes('permission-denied')) {
        setError('You do not have permission to create workspaces. Please check your Firebase security rules.');
      } else if (err.message?.includes('unauthenticated') || err.message?.includes('auth')) {
        setError('You need to be logged in to create workspaces. Please sign in again.');
        // Attempt to re-authenticate
        ensureAuthentication();
      } else {
        setError(err.message || 'Failed to create workspace');
      }
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  // Add an event handler for the close button that will properly handle closing even during loading
  const handleClose = () => {
    // If we're loading, show a confirmation
    if (isLoading) {
      const confirmed = window.confirm(
        'The workspace creation is in progress. Closing this dialog may still result in a workspace being created. Are you sure you want to close?'
      );
      if (confirmed) {
        // Stop the loading state immediately
        setIsLoading(false);
        
        // Reset all form state
        setWorkspaceName('');
        setDescription('');
        setError('');
        
        // Call onClose directly without any timeout
        onClose();
      }
    } else {
      // Reset all form state
      setWorkspaceName('');
      setDescription('');
      setError('');
      
      // Call onClose directly without any timeout
      onClose();
    }
  };

  // Input styling constants - matches Chat component
  const inputStyles = "w-full p-3 rounded-lg border border-[#1e1e1e] bg-[#121212] text-white focus:outline-none focus:ring-2 focus:ring-white transition-colors duration-200";
  const buttonStyles = "px-3 py-2 bg-[#121212] text-white rounded-lg hover:bg-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 transition-colors duration-200 border border-white border-opacity-30 border-[0.25px]";
  const secondaryButtonStyles = "px-3 py-2 border border-white border-opacity-30 border-[0.25px] bg-[#121212] text-white rounded-lg hover:bg-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 transition-colors duration-200";

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog 
        as="div" 
        className="relative z-50" 
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-[#121212] border border-white border-opacity-30 border-[0.25px] p-6 text-left align-middle shadow-xl transition-all">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-white focus:outline-none"
                    onClick={handleClose}
                    disabled={isLoading}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                
                <Dialog.Title
                  as="h3"
                  className="text-xl font-medium leading-6 text-white mb-4"
                >
                  Create New Workspace
                </Dialog.Title>
                
                {/* Network status indicator */}
                {!networkStatus && (
                  <div className="mb-4 p-3 rounded-lg bg-[#300] border border-red-800 flex items-start">
                    <WifiIcon className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">
                      You appear to be offline. Please check your connection and try again.
                    </p>
                      </div>
                    )}
                
                {/* Error message */}
                {error && (
                  <div className="mb-4 p-3 rounded-lg bg-[#300] border border-red-800 flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mt-4 space-y-4">
                  <div>
                      <label htmlFor="workspace-name" className="block text-sm font-medium text-gray-300 mb-1">
                      Workspace Name
                    </label>
                    <input
                      type="text"
                        name="workspace-name"
                        id="workspace-name"
                        className={inputStyles}
                        placeholder="My Workspace"
                      value={workspaceName}
                      onChange={(e) => setWorkspaceName(e.target.value)}
                        disabled={isLoading || !networkStatus}
                      autoFocus
                    />
                  </div>

                  <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-1">
                        Description (Optional)
                    </label>
                    <textarea
                        name="description"
                        id="description"
                        rows={3}
                        className={inputStyles}
                        placeholder="What's this workspace for?"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                        disabled={isLoading || !networkStatus}
                    />
                    </div>
                    </div>

                  <div className="mt-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      className={`${buttonStyles} w-full sm:w-auto sm:ml-3 ${(isLoading || !networkStatus || isSubmittingRef.current) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={isLoading || !networkStatus || !workspaceName.trim() || isSubmittingRef.current}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <span className="mr-2">Creating...</span>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    </div>
                      ) : (
                        'Create Workspace'
                  )}
                    </button>
                    <button
                      type="button"
                      className={`${secondaryButtonStyles} mt-3 sm:mt-0 w-full sm:w-auto`}
                      onClick={handleClose}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CreateWorkspace; 